import React from 'react';
import '../styles/LegalPages.css';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last updated: October 15, 2025</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            METRICBRIDGE, LLC ("we," "our," or "us"), operator of Property ROI, is committed to protecting your 
            privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
            when you use our web application and services.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Personal Information</h3>
          <p>
            We may collect personal information including your name and email address when you create an account with us. We also collect payment information, which is processed securely through Stripe, along with your account preferences and settings. Additionally, we might maintain records of your communication history with our support team to provide better assistance.
          </p>

          <h3>2.2 Property and Usage Data</h3>
          <p>
            We collect information related to your use of our services, including property addresses and details that you analyze through our platform. This encompasses your ROI calculations and analysis results, as well as your search history and saved properties. We also store your chat messages and AI interaction history when you use our conversational features. Additionally, we track your usage patterns and feature utilization to improve our services, along with maintaining records of your subscription tier and billing information.
          </p>

          <h3>2.3 Technical Information</h3>
          <p>
            We automatically collect technical information to ensure our services function properly and to improve your experience. This includes your IP address and location data, browser type and version, as well as device information and operating system details. We also gather usage analytics and performance metrics to monitor and enhance our platform's functionality. Additionally, we use cookies and similar tracking technologies to provide personalized features and analyze user behavior.
          </p>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>
            We use the collected information to provide and maintain our services, ensuring you have access to accurate property analyses and calculations for informational purposes. The information enables us to deliver AI-powered chat assistance and responses tailored to your needs. We utilize your data for managing your account and subscription, processing payments and billing, and sending you important service notifications. Your information also helps us improve our services and user experience by analyzing usage patterns and service performance. Additionally, we use this data to provide comprehensive customer support and to comply with our legal obligations.
          </p>
        </section>

        <section>
          <h2>4. Data Sharing and Disclosure</h2>
          <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
          
          <h3>4.1 Service Providers</h3>
          <p>
            We work with trusted third-party service providers to deliver our services effectively. We utilize AWS (Amazon Web Services) for hosting and infrastructure to ensure reliable platform performance. AWS processes this data in accordance with their privacy policy (https://aws.amazon.com/privacy/). Stripe handles our payment processing to provide secure and convenient billing services, and their data practices are governed by their privacy policy (https://stripe.com/privacy). We partner with Anthropic (Claude AI) to power our chat functionality and provide AI-powered assistance. Additionally, we work with property data providers to access comprehensive real estate information and analytics providers to continuously improve our service quality.
          </p>
          
          <h4>4.1.1 AI Service Provider (Anthropic)</h4>
          <p>
            When you use our chat features, your messages are processed by Anthropic's Claude AI service. This processing is necessary to provide AI-powered responses and assistance. Anthropic processes this data in accordance with their privacy policy (https://www.anthropic.com/privacy) and terms of service (https://www.anthropic.com/terms). We do not have control over Anthropic's data practices, and you should review their privacy policy for more information about how they handle your data.
          </p>

          <h3>4.2 Legal Requirements</h3>
          <p>
            We may disclose your information when required by law or when necessary to comply with legal processes or government requests. We may also share information to protect our rights, property, or safety, as well as to prevent fraud or security threats. Additionally, we may disclose information as needed to enforce our Terms of Service and maintain the integrity of our platform.
          </p>
        </section>

        <section>
          <h2>5. Data Security</h2>
          <p>
            We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. Our security framework includes encryption of data both in transit and at rest to ensure your information remains protected throughout its lifecycle. We conduct regular security assessments and updates to maintain the highest standards of protection. We employ robust access controls and authentication measures to prevent unauthorized system access. Additionally, we ensure secure payment processing through PCI-compliant providers to safeguard your financial information.
          </p>
        </section>

        <section>
          <h2>6. Data Retention</h2>
          <p>
            We retain your information for as long as necessary to provide our services and comply with legal obligations. Your account information is maintained until account deletion plus an additional 30 days for administrative purposes. Property analysis data is retained until account deletion or after 2 years of account inactivity, whichever comes first. Chat message history is preserved until account deletion or after 1 year of inactivity. Payment records are maintained as required by applicable tax and financial regulations to ensure compliance. Usage logs are kept for up to 12 months for service improvement purposes and platform optimization.
          </p>
          <p>
            Note: Data shared with third-party providers (such as Anthropic for AI processing) is subject to their respective data retention policies, which may differ from ours. For details on Anthropic's data retention practices, please refer to their privacy policy at https://www.anthropic.com/privacy.
          </p>
        </section>

        <section>
          <h2>7. Your Rights and Choices</h2>
          <p>
            Depending on your location, you may have several rights regarding your personal information. You have the right to access and request a copy of your personal information that we maintain. You can also request correction or updates to any inaccurate information in your account. You may request deletion of your personal information, subject to certain legal and operational requirements. You have the right to data portability, allowing you to receive your data in a machine-readable format for transfer to other services. Additionally, you can object to certain processing activities and withdraw consent for data processing where consent was the basis for collection.
          </p>
          <p>To exercise these rights, please contact us at privacy@propertyroi.io</p>
        </section>

        <section>
          <h2>8. Cookies and Tracking Technologies</h2>
          <p>
            We use cookies and similar technologies to enhance your experience, analyze usage, 
            and provide personalized content. You can control cookie preferences through your 
            browser settings, though some features may not function properly if cookies are disabled.
          </p>
        </section>

        <section>
          <h2>9. Third-Party Links</h2>
          <p>
            Our service may contain links to third-party websites. We are not responsible for the 
            privacy practices of these external sites. We encourage you to review the privacy 
            policies of any third-party sites you visit.
          </p>
        </section>

        <section>
          <h2>10. Children's Privacy</h2>
          <p>
            Our services are not directed to individuals under 18 years of age. We do not knowingly 
            collect personal information from children. If we become aware that a child has provided 
            personal information, we will take steps to delete such information.
          </p>
        </section>

        <section>
          <h2>11. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your own. 
            We ensure appropriate safeguards are in place to protect your information in accordance 
            with applicable data protection laws.
          </p>
        </section>

        <section>
          <h2>12. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy periodically to reflect changes in our practices or 
            applicable laws. We will notify you of material changes by posting the updated policy 
            on our website and updating the "Last updated" date above.
          </p>
        </section>

        <section>
          <h2>13. Contact Information</h2>
          <p>
            If you have questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <p>
            Email: privacy@propertyroi.io<br/>
            Support: support@propertyroi.io
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;